import {
  useQuery,
  useMutation,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { httpService } from '@services/httpService';
import { ApiRoute } from '@routes/apiRoutes';
import { QueryKeys } from '@enums/queryKeys';
import { GetDrawRequestMessagesModel } from '@models/drawRequests/GetDrawRequestMessagesModel';
import { SendDrawRequestMessageRequest } from '@models/drawRequests/SendDrawRequestMessageRequest';
import { DrawRequestMessage } from '@models/drawRequests/drawRequestMessage';
import { BulkSaveDrawRequestMessagesModel } from '@models/drawRequests/BulkSaveDrawRequestMessagesModel';
import { DrawRequestMessageModel } from '@models/drawRequests/DrawRequestMessageModel';
import { OriginatorTypes } from '@enums/originatorTypes';
import { RecipientTypes } from '@enums/recipientTypes';
import {
  MESSAGES_DEFAULT_OFFSET,
  MESSAGES_DEFAULT_LIMIT,
  MESSAGES_LOAD_MORE_LIMIT,
} from '@constants/drawManagerConstants';

const fetchDrawRequestMessages = async (
  drawRequestId: string,
  sourceRecId: string,
  offset: number = MESSAGES_DEFAULT_OFFSET,
  limit: number = MESSAGES_DEFAULT_LIMIT
): Promise<DrawRequestMessage[]> => {
  try {
    const request: GetDrawRequestMessagesModel = {
      drawRequestId,
      sourceRecId,
      offset,
      limit,
    };

    const response = await httpService.post<DrawRequestMessageModel[]>(
      ApiRoute.DrawRequestMessages,
      request
    );

    const messages = (response || []).map(msg => ({
      recId: msg.recId || '',
      message: msg.message || '',
      sentDateTime: msg.sentDateTime ? new Date(msg.sentDateTime) : new Date(),
      originatorType:
        (msg.originatorType as OriginatorTypes) || OriginatorTypes.None,
      recipientType:
        (msg.recipientType as RecipientTypes) || RecipientTypes.None,
      readDateTime: msg.readDateTime
        ? new Date(msg.readDateTime)
        : null,
    }));

    return messages;
  } catch (error) {
    console.error('Error fetching messages:', error);
    throw error;
  }
};

const sendDrawRequestMessage = async (
  request: SendDrawRequestMessageRequest
): Promise<{ success: boolean }> => {
  try {
    return await httpService.post<{ success: boolean }>(
      ApiRoute.DrawRequestSendMessage,
      request
    );
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

const saveBulkMessages = async (
  request: BulkSaveDrawRequestMessagesModel
): Promise<void> => {
  try {
    await httpService.post<{ success: boolean }>(
      ApiRoute.DrawRequestBulkSaveMessages,
      request
    );
  } catch (error) {
    console.error('Error saving messages:', error);
    throw error;
  }
};

const markMessagesAsRead = async (request: {
  drawRequestId: string;
  sourceRecId: string;
}): Promise<void> => {
  try {
    await httpService.post<void>(ApiRoute.DrawRequestMarkAsRead, request);
  } catch (error) {
    console.error('Error marking messages as read:', error);
    throw error;
  }
};

const getUnreadCount = async (request: {
  drawRequestId: string;
  sourceRecId: string;
}): Promise<number> => {
  try {
    const response = await httpService.post<number>(
      ApiRoute.DrawRequestUnreadCount,
      request
    );
    return response || 0;
  } catch (error) {
    console.error('Error fetching unread count:', error);
    throw error;
  }
};

export const useDrawRequestMessageApi = {
  useFetchMessages: (
    drawRequestId: string,
    sourceRecId: string,
    offset: number = MESSAGES_DEFAULT_OFFSET,
    limit: number = MESSAGES_DEFAULT_LIMIT
  ) => {
    return useQuery<DrawRequestMessage[], Error>({
      queryKey: [
        QueryKeys.DRAW_REQUEST_MESSAGES,
        drawRequestId,
        sourceRecId,
        offset,
        limit,
      ],
      queryFn: () =>
        fetchDrawRequestMessages(drawRequestId, sourceRecId, offset, limit),
      enabled: !!drawRequestId && !!sourceRecId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      retry: 2,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    });
  },

  useSendMessage: () => {
    return useMutation<
      { success: boolean },
      Error,
      SendDrawRequestMessageRequest
    >({
      mutationFn: sendDrawRequestMessage,
      retry: 1,
      retryDelay: 1000,
    });
  },

  useSaveBulkMessages: () => {
    return useMutation<void, Error, BulkSaveDrawRequestMessagesModel>({
      mutationFn: saveBulkMessages,
      retry: 1,
      retryDelay: 1000,
    });
  },

  useFetchMessagesInfinite: (drawRequestId: string, sourceRecId: string) => {
    return useInfiniteQuery<DrawRequestMessage[], Error>({
      queryKey: [
        QueryKeys.DRAW_REQUEST_MESSAGES,
        drawRequestId,
        sourceRecId,
        'infinite',
      ],
      queryFn: ({ pageParam = 0 }) =>
        fetchDrawRequestMessages(
          drawRequestId,
          sourceRecId,
          pageParam as number,
          MESSAGES_LOAD_MORE_LIMIT
        ),
      initialPageParam: 0,
      enabled: !!drawRequestId && !!sourceRecId,
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.length < MESSAGES_LOAD_MORE_LIMIT) {
          return undefined;
        }
        return allPages.length * MESSAGES_LOAD_MORE_LIMIT;
      },
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      retry: 2,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    });
  },

  useMarkAsRead: () => {
    const queryClient = useQueryClient();

    return useMutation<
      void,
      Error,
      { drawRequestId: string; sourceRecId: string }
    >({
      mutationFn: markMessagesAsRead,
      retry: 1,
      retryDelay: 1000,
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.DRAW_REQUESTS],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeys.DRAW_REQUEST_MESSAGES,
            variables.drawRequestId,
            variables.sourceRecId,
          ],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeys.DRAW_REQUEST_UNREAD_COUNT,
            variables.drawRequestId,
            variables.sourceRecId,
          ],
        });
      },
    });
  },

  useFetchUnreadCount: (
    drawRequestId: string,
    sourceRecId: string,
    enabled?: boolean
  ) => {
    return useQuery<number, Error>({
      queryKey: [
        QueryKeys.DRAW_REQUEST_UNREAD_COUNT,
        drawRequestId,
        sourceRecId,
      ],
      queryFn: () => getUnreadCount({ drawRequestId, sourceRecId }),
      enabled: (enabled ?? true) && !!drawRequestId && !!sourceRecId,
      staleTime: 30 * 1000,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      retry: 2,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    });
  },
};
